/* eslint-disable camelcase */
import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    makeStyles,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography
} from '@material-ui/core';
import { LoadingButton } from '@swipejobs/fe-service-desktop-ui-components';
import {
    DocumentTypeOptionsEnum,
    I9DocumentTypesEnum,
    useUploadDocumentContext
} from '@swipejobs/i9-document-validations-react';
import { capitalize } from 'lodash-es';
import { UploadProgressEnum } from './I9DocumentUploadV1';
import UploadButton from './UploadButton';
import DocumentImagePlaceholder from './DocumentImagePlaceholder';
import { cacheTranslations } from '@utils/cache-translations';
import { createTranslationService } from '@services/factories';
import { interpolatePath } from '@utils/PathInterpolation';
import DocumentExpiryDateField from '../DocumentExpiryDateField';

const useStyles = makeStyles((theme) => ({
    root: {
        width: 700,
        padding: 10
    },
    image: {
        width: 'auto',
        height: '100%',
        maxWidth: '100%',
        padding: '16px 0'
    },
    scrollContainer: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowY: 'auto'
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        marginTop: 10,
        marginBottom: 10
    },
    list: {
        '@media (max-height: 600px)': {
            fontSize: 12
        }
    },
    leftAlign: {
        '@media (max-width: 768px)': {
            marginTop: 2,
            marginBottom: 2
        },
        marginTop: 10,
        marginBottom: 10,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'left',
        width: '100%'
    },
    highlightedCaption: {
        whiteSpace: 'normal',
        fontWeight: 'bold',
        marginBottom: '8px'
    },
    inputContainer: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
        width: '100%'
    },
    documentTypeList: {
        width: '100%'
    },
    divider: {
        margin: theme.spacing(2, 0)
    },
    actions: {
        justifyContent: 'left',
        padding: theme.spacing(0, 3, 3)
    },
    uploadDocPlaceholderContainer: {
        height: '250px',
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        margin: '16px 0'
    },
    errorText: {
        color: '#FF5280',
        fontStyle: 'italic',
        fontSize: '12px',
        textAlign: 'center'
    },
    inputWrapper: {
        margin: '24px 0'
    },
    inputFieldContainer: {
        // marginLeft: '8px',
    },
  uploadDocumentsActionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 24px',
  }
}));

enum Resources {
    I9_LIST_A = 'I9_LIST_A',
    I9_LIST_B = 'I9_LIST_B',
    I9_LIST_C = 'I9_LIST_C',
    loadingDocumentOptions = 'loadingDocumentOptions',
    selectDocumentTypeLabel = 'selectDocumentTypeLabel',
    issuingAuthorityLabel = 'issuingAuthorityLabel',
    requiredFieldLabel = 'requiredFieldLabel',
    enterIssuingFieldPlaceholder = 'enterIssuingFieldPlaceholder',
    selectIssuingFieldPlaceholder = 'selectIssuingFieldPlaceholder',
    enterDocumentNumberLabel = 'enterDocumentNumberLabel',
    invalidDocumentNumberLabel = 'invalidDocumentNumberLabel',
    expiryDateLabel = 'expiryDateLabel',
    NA = 'NA',
    uploadLabel = 'uploadLabel',
    uploadDoubleSideLabel = 'uploadDoubleSideLabel',
    uploadButtonLabel = 'uploadButtonLabel',
    requiredImage = 'requiredImage',
    doneLabel = 'doneLabel',
    addDocument = 'addDocument',
    cancelButton = 'cancelButton',
    selectMissingIssuingAuthority = 'selectMissingIssuingAuthority',
    enterMissingIssuingAuthority = 'enterMissingIssuingAuthority',
    selectDocumentTypeOptionLabel = 'selectDocumentTypeOptionLabel',
    missingDocumentTypeOptionLabel = 'missingDocumentTypeOptionLabel',
    uploadDifferentDocuments = 'uploadDifferentDocuments'
}

interface DocumentUploadModalV1Props {
    open: boolean;
    onClose: () => void;
    i9Section2Status?: string;
    handleArchiveReceipts?: () => void;
}

const DocumentUploadModalV1 = (props: DocumentUploadModalV1Props) => {
    const { open, onClose, i9Section2Status, handleArchiveReceipts } = props;
    const css = useStyles();

    const oldResources = cacheTranslations(
        createTranslationService(),
        {
            title: 'title',
            instructionsLabel: 'instructionsLabel',
            documentInfoLine1: 'documentInfoLine1',
            documentInfoLine2: 'documentInfoLine2',
            documentInfoLine3: 'documentInfoLine3',
            documentInfoLine4: 'documentInfoLine4'
        },
        'i9DocumentUpload'
    );

    const resources = cacheTranslations(
        createTranslationService(),
        Resources,
        'i9DocumentUpload.uploadSingleDocumentPort'
    );

    const {
        handleSaveInProgressDocument,
        handleSelectDocument,
        handleUpdateCurrentUploadingField,
        handleUpdateDocumentOption,
        isValidField,
        savedDocuments,
        getNextDocumentOptions,
        possibleDocumentTypeOptions
    } = useUploadDocumentContext();
    const [clickedSaved, setClickedSaved] = useState(false);

    const documentUploadState = savedDocuments.find(
        (i) => i.progress !== UploadProgressEnum.DONE
    );
    if (!documentUploadState) return <></>;

    const uploadDocumentSelectOptions = getNextDocumentOptions();
    const documentInfo = documentUploadState?.document;

    const getActiveDocumentTypeOptions = possibleDocumentTypeOptions();

    const getHelperText = () => {
        if (!(clickedSaved && !!documentInfo)) return [];
        return !isValidField(documentInfo?.documentType).documentNumber
            ? documentInfo.documentNumberRequirements.split(';')
            : [];
    };

    // @ts-ignore
    return (
        <Dialog
            maxWidth='md'
            open={open}
            onClose={() => {
                setClickedSaved(false);
                onClose();
            }}
        >
            <DialogTitle>{oldResources.title}</DialogTitle>
            <DialogContent style={{ maxHeight: '500px' }}>
                <Typography variant='subtitle1'>
                    {oldResources.instructionsLabel}
                </Typography>
                <ul className={css.list}>
                    <li>{oldResources.documentInfoLine1}</li>
                    <li>{oldResources.documentInfoLine2}</li>
                    {documentInfo?.documentBackRequired && (
                        <li>{oldResources.documentInfoLine3}</li>
                    )}
                    <li>{oldResources.documentInfoLine4}</li>
                </ul>

                <Box className={css.inputWrapper}>
                    <Typography
                        component='label'
                        htmlFor='document-select'
                        className={css.highlightedCaption}
                    >
                        {oldResources.documentInfoLine1}
                    </Typography>
                    <FormControl
                        className={css.inputFieldContainer}
                        fullWidth={true}
                        variant='outlined'
                    >
                        <Select
                            id='document-select'
                            value={
                                ([
                                    DocumentTypeOptionsEnum.RECEIPT,
                                    DocumentTypeOptionsEnum.EXTENSION
                                ].includes(
                                    documentUploadState?.documentTypeOptions as DocumentTypeOptionsEnum
                                )
                                    ? documentUploadState.actualDocumentType
                                    : documentUploadState?.document
                                          ?.documentType) ?? ''
                            }
                            onChange={({ target: { value } }) => {
                                handleSelectDocument(
                                    value as I9DocumentTypesEnum,
                                    documentInfo?.documentType
                                );
                                setClickedSaved(false);
                            }}
                            disabled={
                                documentUploadState?.progress ===
                                UploadProgressEnum.EDITING
                            }
                        >
                            {uploadDocumentSelectOptions?.options?.map(
                                (doc) => (
                                    <MenuItem
                                        selected={
                                            documentInfo?.documentType ===
                                            doc.documentType
                                        }
                                        value={doc.documentType}
                                        key={doc.documentType}
                                    >
                                        {doc.title}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                </Box>

                {/* document options */}
                {documentInfo && (
                    <>
                        {getActiveDocumentTypeOptions?.length > 1 && (
                            <div className={css.inputWrapper}>
                                <Typography
                                    style={{ marginBottom: 0 }}
                                    className={css.highlightedCaption}
                                >
                                    {resources.selectDocumentTypeOptionLabel}
                                </Typography>
                                <FormControl
                                    error={
                                        clickedSaved &&
                                        !isValidField(documentInfo.documentType)
                                            .documentTypeOptions
                                    }
                                    className={css.inputFieldContainer}
                                >
                                    <RadioGroup
                                        row={true}
                                        onChange={(_e, value) => {
                                            handleUpdateDocumentOption(
                                                value as DocumentTypeOptionsEnum,
                                                documentInfo.documentType
                                            );
                                        }}
                                        value={
                                            documentUploadState?.documentTypeOptions ??
                                            ''
                                        }
                                    >
                                        {getActiveDocumentTypeOptions.map(
                                            (option) => (
                                                <FormControlLabel
                                                    disabled={
                                                        documentUploadState?.progress ===
                                                        UploadProgressEnum.EDITING ||
                                                        (option === DocumentTypeOptionsEnum.RECEIPT && i9Section2Status === 'RECEIPT_REPLACEMENT')
                                                    }
                                                    value={option}
                                                    control={<Radio />}
                                                    label={capitalize(option)}
                                                    key={option}
                                                />
                                            )
                                        )}
                                    </RadioGroup>
                                    {clickedSaved &&
                                        !isValidField(documentInfo.documentType)
                                            .documentTypeOptions && (
                                            <FormHelperText
                                                style={{ textAlign: 'left' }}
                                            >
                                                {
                                                    resources.missingDocumentTypeOptionLabel
                                                }
                                            </FormHelperText>
                                        )}
                                </FormControl>
                            </div>
                        )}

                        {/* ----- */}

                        {(getActiveDocumentTypeOptions?.length > 1
                            ? !!documentUploadState.documentTypeOptions
                            : true) && (
                            <>
                                <div className={css.inputWrapper}>
                                    <Typography
                                        className={css.highlightedCaption}
                                    >
                                        {documentInfo.freeTextAllowed
                                            ? resources.enterIssuingFieldPlaceholder
                                            : resources.selectIssuingFieldPlaceholder}
                                    </Typography>
                                    <FormControl
                                        className={css.inputFieldContainer}
                                        fullWidth={true}
                                    >
                                        {documentInfo.freeTextAllowed ? (
                                            <TextField
                                                fullWidth={true}
                                                variant='outlined'
                                                error={
                                                    clickedSaved &&
                                                    !isValidField(
                                                        documentInfo.documentType
                                                    ).issuingAuthority
                                                }
                                                helperText={
                                                    clickedSaved &&
                                                    !isValidField(
                                                        documentInfo.documentType
                                                    ).issuingAuthority
                                                        ? resources.enterMissingIssuingAuthority
                                                        : ''
                                                }
                                                onChange={({
                                                    target: { value }
                                                }) =>
                                                    handleUpdateCurrentUploadingField(
                                                        'issuingAuthority',
                                                        value,
                                                        documentInfo.documentType
                                                    )
                                                }
                                                value={
                                                    documentUploadState?.issuingAuthority ??
                                                    ''
                                                }
                                                placeholder={
                                                    resources.enterIssuingFieldPlaceholder
                                                }
                                            />
                                        ) : (
                                            <FormControl
                                                error={
                                                    clickedSaved &&
                                                    !isValidField(
                                                        documentInfo.documentType
                                                    ).issuingAuthority
                                                }
                                                className={
                                                    css.inputFieldContainer
                                                }
                                                variant='outlined'
                                                fullWidth={true}
                                            >
                                                <Select
                                                    variant='outlined'
                                                    fullWidth={true}
                                                    value={
                                                        documentUploadState.issuingAuthorityCode ??
                                                        ''
                                                    }
                                                    error={
                                                        clickedSaved &&
                                                        !isValidField(
                                                            documentInfo.documentType
                                                        ).issuingAuthorityCode
                                                    }
                                                    onChange={({
                                                        target: { value }
                                                    }) => {
                                                        const issuingAuthorityName =
                                                            documentInfo.issuingAuthorities?.find(
                                                                (i) =>
                                                                    i?.code ===
                                                                    value
                                                            )?.displayName ??
                                                            '';
                                                        handleUpdateCurrentUploadingField(
                                                            'issuingAuthority',
                                                            issuingAuthorityName,
                                                            documentInfo.documentType
                                                        );
                                                        handleUpdateCurrentUploadingField(
                                                            'issuingAuthorityCode',
                                                            value as string,
                                                            documentInfo.documentType
                                                        );
                                                    }}
                                                >
                                                    {documentInfo?.issuingAuthorities?.map(
                                                        (authority) => (
                                                            <MenuItem
                                                                value={
                                                                    authority.code
                                                                }
                                                                selected={
                                                                    documentUploadState.issuingAuthority ===
                                                                    authority.code
                                                                }
                                                                key={
                                                                    authority.code
                                                                }
                                                            >
                                                                {
                                                                    authority.displayName
                                                                }
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                                {clickedSaved &&
                                                    !isValidField(
                                                        documentInfo.documentType
                                                    ).issuingAuthority && (
                                                        <FormHelperText>
                                                            {
                                                                resources.selectMissingIssuingAuthority
                                                            }
                                                        </FormHelperText>
                                                    )}
                                            </FormControl>
                                        )}
                                    </FormControl>
                                </div>

                                <div className={css.inputWrapper}>
                                    <Typography
                                        className={css.highlightedCaption}
                                    >
                                        Document Number
                                    </Typography>
                                    <FormControl
                                        className={css.inputFieldContainer}
                                        fullWidth={true}
                                        variant='outlined'
                                    >
                                        <TextField
                                            variant='outlined'
                                            onChange={({
                                                target: { value }
                                            }) => {
                                                handleUpdateCurrentUploadingField(
                                                    'documentNumber',
                                                    value,
                                                    documentInfo.documentType
                                                );
                                            }}
                                            helperText={
                                                getHelperText().length > 0 && (
                                                    <ul>
                                                        {getHelperText().map(
                                                            (
                                                                message,
                                                                index
                                                            ) => (
                                                                <li key={index}>
                                                                    {message}
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                )
                                            }
                                            error={
                                                clickedSaved &&
                                                !isValidField(
                                                    documentInfo.documentType
                                                ).documentNumber
                                            }
                                            value={
                                                documentUploadState?.documentNumber ??
                                                ''
                                            }
                                            placeholder={
                                                documentUploadState.document
                                                    ?.documentNumberExample ??
                                                ''
                                            }
                                        />
                                    </FormControl>
                                </div>

                                <DocumentExpiryDateField
                                    documentInfo={documentInfo}
                                    expiryDate={
                                        documentUploadState.documentExpiry
                                    }
                                    checkedNoExpiry={
                                        documentUploadState.documentExpiryNotApplicable
                                    }
                                    error={
                                        clickedSaved &&
                                        !(
                                            isValidField(
                                                documentInfo.documentType
                                            ).documentExpiry &&
                                            isValidField(
                                                documentInfo.documentType
                                            ).documentExpiryNotApplicable
                                        )
                                    }
                                    onDateChange={(date) =>
                                        handleUpdateCurrentUploadingField(
                                            'documentExpiry',
                                            date
                                        )
                                    }
                                    onNoExpiryCheckChange={(checked) =>
                                        handleUpdateCurrentUploadingField(
                                            'documentExpiryNotApplicable',
                                            checked
                                        )
                                    }
                                    labelProps={{
                                        className: css.highlightedCaption
                                    }}
                                />

                                <div className={css.inputWrapper}>
                                    <Typography>
                                        {resources.uploadLabel}
                                    </Typography>
                                    <Grid
                                        style={{
                                            justifyContent: 'center',
                                            marginTop: '12px'
                                        }}
                                        container={true}
                                        spacing={2}
                                    >
                                        <Grid
                                            item={true}
                                            xs={6}
                                            style={{ textAlign: 'center' }}
                                        >
                                            <Box
                                                className={
                                                    css.uploadDocPlaceholderContainer
                                                }
                                            >
                                                <Typography>
                                                    {documentInfo.description}
                                                </Typography>
                                                <DocumentImagePlaceholder
                                                    uploadedImage={
                                                        documentUploadState.base64FileFront
                                                    }
                                                    description={
                                                        documentInfo.description
                                                    }
                                                    placeholderImageUrl={
                                                        documentInfo.exampleImageUrl
                                                    }
                                                />
                                            </Box>
                                            <UploadButton
                                                buttonTitle={interpolatePath(
                                                    resources.uploadButtonLabel,
                                                    {
                                                        documentName:
                                                            documentInfo.description
                                                    }
                                                )}
                                                id='document-1-front'
                                                onChange={(
                                                    base64FileFront: string
                                                ) =>
                                                    handleUpdateCurrentUploadingField(
                                                        'base64FileFront',
                                                        base64FileFront,
                                                        documentInfo.documentType
                                                    )
                                                }
                                            />
                                        </Grid>

                                        {documentInfo.documentBackRequired && (
                                            <>
                                                <Grid item={true} xs={1}>
                                                    <Divider
                                                        style={{
                                                            margin: '0 auto'
                                                        }}
                                                        variant='fullWidth'
                                                        orientation='vertical'
                                                    />
                                                </Grid>
                                                <Grid
                                                    item={true}
                                                    xs={5}
                                                    style={{
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    <Box
                                                        className={
                                                            css.uploadDocPlaceholderContainer
                                                        }
                                                    >
                                                        <Typography>
                                                            {
                                                                documentInfo.descriptionBack
                                                            }
                                                        </Typography>
                                                        <DocumentImagePlaceholder
                                                            uploadedImage={
                                                                documentUploadState.base64FileBack
                                                            }
                                                            description={
                                                                documentInfo.description
                                                            }
                                                            placeholderImageUrl={
                                                                documentInfo.exampleImageUrlBack
                                                            }
                                                        />
                                                    </Box>
                                                    <UploadButton
                                                        buttonTitle={interpolatePath(
                                                            resources.uploadButtonLabel,
                                                            {
                                                                documentName:
                                                                    documentInfo.descriptionBack
                                                            }
                                                        )}
                                                        id='document-1-back'
                                                        onChange={(
                                                            base64FileBack
                                                        ) =>
                                                            handleUpdateCurrentUploadingField(
                                                                'base64FileBack',
                                                                base64FileBack,
                                                                documentInfo.documentType
                                                            )
                                                        }
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </div>
                            </>
                        )}
                    </>
                )}
                {/* ------ */}
            </DialogContent>
            <DialogActions className={css.uploadDocumentsActionContainer}>
                {i9Section2Status === 'RECEIPT_REPLACEMENT' && (
                    <Box>
                        <LoadingButton
                            variant='outlined'
                            color='primary'
                            size='medium'
                            onClick={() => {
                                // @ts-ignore
                                handleArchiveReceipts();
                            }}
                        >
                            {resources.uploadDifferentDocuments}
                        </LoadingButton>
                    </Box>
                )}
                <Box display='flex'>
                    <Box style={{ paddingRight: '8px' }}>
                        <Button
                            variant='outlined'
                            size='medium'
                            onClick={() => {
                                onClose();
                            }}
                            color='primary'
                        >
                            {resources.cancelButton}
                        </Button>
                    </Box>
                </Box>
                <Box>
                    <LoadingButton
                        variant='contained'
                        color='primary'
                        size='medium'
                        onClick={() => {
                            setClickedSaved(true);
                            // @ts-ignore
                            handleSaveInProgressDocument(documentInfo?.documentType);
                        }}
                    >
                        {resources.addDocument}
                    </LoadingButton>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default DocumentUploadModalV1;



// WEBPACK FOOTER //
// ./src/screens/review-section2/document-upload/v2/DocumentUploadModalV1.tsx